import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Create context
const SupabaseContext = createContext();

export const useAuth = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useAuth must be used within a SupabaseProvider');
  }
  return context;
};

export const SupabaseProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      setLoading(true);

      try {
        // Check for active session
        const { data: { session }, error } = await supabase.auth.getSession();

        // Ignore session-related errors during initialization
        if (error &&
            !error.message?.includes('session_not_found') &&
            !error.message?.includes('AuthSessionMissingError')) {
          setError(error);
        }

        setSession(session);
        setUser(session?.user || null);
      } catch (err) {
        // Silently ignore session errors during initialization
        if (!err.message?.includes('session_not_found') &&
            !err.message?.includes('AuthSessionMissingError')) {
          console.warn('Session initialization error:', err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Handle different auth events
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          setSession(session);
          setUser(session?.user || null);
        } else {
          // For other events, just update the session
          setSession(session);
          setUser(session?.user || null);
        }

        setLoading(false);
      }
    );

    // Cleanup subscription
    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  // Sign up with email and password
const signUp = async (email, password, userData = {}, captchaToken = null) => {
  try {
    setLoading(true);

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData,
        captchaToken,
      },
    });

    if (error) {
      if (
        error.message.includes('already registered') ||
        error.message.includes('already exists') ||
        error.message.includes('already taken')
      ) {
        throw new Error('This email is already registered. Please log in or reset your password.');
      }

      throw error;
    }

    if (!data?.user?.id) {
      throw new Error('Signup failed. Please try again.');
    }

    // We'll let the RegisterPage component handle navigation
    // This allows the RegisterPage to check if the email exists first
    // and only navigate to check-email if the signup was successful
    return data;
  } catch (err) {
    setError(err);
    throw err;
  } finally {
    setLoading(false);
  }
};

  // Sign in with email and password
  const signIn = async (email, password, captchaToken = null) => {
    try {
      setLoading(true);
      
      const signInOptions = {
        email,
        password,
      };
      
      // Add captchaToken to options if provided
      if (captchaToken) {
        signInOptions.options = {
          captchaToken,
        };
      }
      
      const { data, error } = await supabase.auth.signInWithPassword(signInOptions);
      
      if (error) throw error;
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with social provider
  const signInWithSocial = async (provider) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setLoading(true);

      // Clear local state first to prevent UI issues
      const userIdForCleanup = user?.id;
      setSession(null);
      setUser(null);

      // Clear cached role from localStorage
      if (userIdForCleanup) {
        localStorage.removeItem(`userRole_${userIdForCleanup}`);
      }

      try {
        // Attempt to sign out from Supabase
        const { error } = await supabase.auth.signOut();

        // Only log/throw error if it's NOT a session-related error
        if (error &&
            !error.message?.includes('session_not_found') &&
            !error.message?.includes('AuthSessionMissingError') &&
            !error.message?.includes('Auth session missing')) {
          console.warn('Error during Supabase signOut (non-critical):', error);
        }
      } catch (signOutError) {
        // Ignore session-related errors during signOut
        if (!signOutError.message?.includes('session_not_found') &&
            !signOutError.message?.includes('AuthSessionMissingError') &&
            !signOutError.message?.includes('Auth session missing')) {
          console.warn('Error during Supabase signOut (non-critical):', signOutError);
        }
      }

      // Always navigate to home after clearing state
      navigate('/');
    } catch (error) {
      // This catch is for any other unexpected errors
      console.error('Unexpected error during logout:', error);
      // Still clear state and navigate even on error
      setSession(null);
      setUser(null);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (email, captchaToken = null) => {
    try {
      setLoading(true);
      
      const resetOptions = {
        redirectTo: `${window.location.origin}/reset-password`,
      };
      
      // Add captchaToken to options if provided
      if (captchaToken) {
        resetOptions.captchaToken = captchaToken;
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, resetOptions);
      
      if (error) throw error;
      return true;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.updateUser({
        data: userData,
      });
      
      if (error) throw error;
      setUser({ ...user, ...data.user });
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Refresh the session to ensure changes are reflected
  const refreshSession = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) throw error;
      
      // Update session and user state with the refreshed data
      setSession(data.session);
      setUser(data.session?.user || null);
      
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get user role from Supabase
  const getUserRole = async () => {
    if (!user) return null;
    
    try {
      // First, check if we have the role in localStorage to avoid unnecessary queries
      const cachedRole = localStorage.getItem(`userRole_${user.id}`);
      if (cachedRole) {
        return cachedRole;
      }
      
      // Query the user roles table with a timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // Increase timeout to 10 seconds
      
      console.log('Fetching user role for user ID:', user.id);
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .single()
        .abortSignal(controller.signal);
      
      clearTimeout(timeoutId);
      
      if (error) {
        if (error.code === 'PGRST301' || error.message.includes('abort')) {
          console.error('User role query timed out - defaulting to User role for security');
          console.error('Timeout error details:', error);

          // SECURITY: Always default to User role (least privilege)
          // Never auto-grant admin access, even in development
          const defaultRole = 'User';
          localStorage.setItem(`userRole_${user.id}`, defaultRole);
          return defaultRole;
        }

        // For other errors, log them and throw
        console.error('Error fetching user role:', error);
        throw error;
      }
      
      // Cache the role in localStorage to reduce queries
      const role = data?.role || 'User';
      localStorage.setItem(`userRole_${user.id}`, role);
      
      // Return the actual role from the database
      return role;
    } catch (error) {
      console.error('Error getting user role:', error);

      // SECURITY: Always default to User role (least privilege)
      // Never auto-grant admin access, even in development
      return 'User';
    }
  };

  // Check if user has any of the specified roles
  const hasAnyRole = async (roles = []) => {
    if (!user) return false;

    try {
      const userRole = await getUserRole();

      // Admin has access to everything
      if (userRole === 'Admin') return true;

      // Check if user's role is in the required roles array
      return roles.includes(userRole);
    } catch (error) {
      console.error('Error checking user roles:', error);

      // SECURITY: Always deny access on error (fail-safe)
      // Never auto-grant access, even in development
      return false;
    }
  };

  // Safe method to get session - handles AuthSessionMissingError gracefully
  const getSessionSafely = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      // Ignore session-related errors
      if (error &&
          error.message?.includes('session_not_found') ||
          error.message?.includes('AuthSessionMissingError')) {
        return { data: { session: null }, error: null };
      }

      if (error) {
        return { data: { session: null }, error };
      }

      return { data: { session }, error: null };
    } catch (err) {
      // Silently handle session errors
      if (err.message?.includes('session_not_found') ||
          err.message?.includes('AuthSessionMissingError')) {
        return { data: { session: null }, error: null };
      }

      return { data: { session: null }, error: err };
    }
  };

  const value = {
    user,
    session,
    loading,
    error,
    signUp,
    signIn,
    signInWithSocial,
    signOut,
    resetPassword,
    updateProfile,
    refreshSession,
    getUserRole,
    hasAnyRole,
    getSessionSafely, // Expose safe session getter
    supabase, // Expose supabase client for direct access if needed
    isAuthenticated: !!user,
  };

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
};

export default SupabaseProvider;
